import React, { useState, useEffect, useCallback } from 'react';
import {
  Music, Users, BookOpen, CreditCard, BarChart2,
  Plus, Search, Edit, X,
  RefreshCw, User, ChevronRight, Tag
} from 'lucide-react';
import ArtSchoolTeacherModal  from './modals/ArtSchoolTeacherModal';
import ArtSchoolClassModal    from './modals/ArtSchoolClassModal';
import ArtSchoolFamilyModal   from './modals/ArtSchoolFamilyModal';
import ArtSchoolEnrollModal   from './modals/ArtSchoolEnrollModal';
import ArtSchoolReportModal   from './modals/ArtSchoolReportModal';

// ----------------------------------------------------------------
// Tabs
// ----------------------------------------------------------------
const ALL_TABS = [
  { id: 'dashboard',   label: 'Dashboard',   icon: BarChart2,  adminOnly: true  },
  { id: 'classes',     label: 'Classes',     icon: Music,      adminOnly: true  },
  { id: 'families',    label: 'Families',    icon: Users,      adminOnly: false },
  { id: 'enrollments', label: 'Enrollments', icon: BookOpen,   adminOnly: true  },
  { id: 'payments',    label: 'Payments',    icon: CreditCard, adminOnly: true  }
];

const ArtSchool = ({ apiCall, selectedYear, hasRole, showSuccess, showError }) => {
  const canAdmin  = hasRole('GlobalAdmin') || hasRole('ArtSchoolAdmin');
  const canEnroll = hasRole('GlobalAdmin') || hasRole('ArtSchoolAdmin') || hasRole('ArtSchool');
  const canPay    = hasRole('GlobalAdmin') || hasRole('ArtSchoolAdmin') || hasRole('ArtSchoolCashier');
  const canReport = hasRole('GlobalAdmin') || hasRole('ArtSchoolAdmin');

  // ArtSchool-only users see only the Families tab
  const TABS = canAdmin ? ALL_TABS : ALL_TABS.filter(t => !t.adminOnly);

  const [activeTab, setActiveTab] = useState(canAdmin ? 'dashboard' : 'families');

  // Data
  const [categories, setCategories]     = useState([]);
  const [teachers, setTeachers]         = useState([]);
  const [classes, setClasses]           = useState([]);
  const [enrollments, setEnrollments]   = useState([]);
  const [payments, setPayments]         = useState([]);
  const [dashboard, setDashboard]       = useState(null);

  // Family/student drill-down
  const [familySearch, setFamilySearch]     = useState('');
  const [familyResults, setFamilyResults]   = useState([]);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [familyDetail, setFamilyDetail]     = useState(null);
  const [searching, setSearching]           = useState(false);

  // Modals
  const [teacherModal, setTeacherModal]   = useState(false);
  const [editTeacher, setEditTeacher]     = useState(null);
  const [classModal, setClassModal]       = useState(false);
  const [editClass, setEditClass]         = useState(null);
  const [familyModal, setFamilyModal]     = useState(false);
  const [enrollModal, setEnrollModal]     = useState(null);  // { student, family }
  const [reportModal, setReportModal]     = useState(false);

  // Filters
  const [classFilter, setClassFilter]   = useState('');
  const [statusFilter, setStatusFilter] = useState('Active');

  // ----------------------------------------------------------------
  // Data loaders
  // ----------------------------------------------------------------
  const loadDashboard = useCallback(async () => {
    try {
      const data = await apiCall(`/artschool/dashboard?academicYear=${selectedYear}`);
      setDashboard(data);
    } catch { /* silent */ }
  }, [apiCall, selectedYear]);

  const loadCategories = useCallback(async () => {
    try { setCategories(await apiCall('/artschool/categories')); } catch { /* */ }
  }, [apiCall]);

  const loadTeachers = useCallback(async () => {
    try { setTeachers(await apiCall('/artschool/teachers')); } catch { /* */ }
  }, [apiCall]);

  const loadClasses = useCallback(async () => {
    try {
      setClasses(await apiCall(`/artschool/classes?academicYear=${selectedYear}`));
    } catch { /* */ }
  }, [apiCall, selectedYear]);

  const loadEnrollments = useCallback(async () => {
    try {
      const params = new URLSearchParams({ academicYear: selectedYear });
      if (statusFilter) params.set('status', statusFilter);
      if (classFilter)  params.set('classID', classFilter);
      setEnrollments(await apiCall(`/artschool/enrollments?${params}`));
    } catch { /* */ }
  }, [apiCall, selectedYear, statusFilter, classFilter]);

  const loadPayments = useCallback(async () => {
    try {
      setPayments(await apiCall(`/artschool/payments?academicYear=${selectedYear}`));
    } catch { /* */ }
  }, [apiCall, selectedYear]);

  const loadFamilyDetail = useCallback(async (familyID) => {
    try {
      const data = await apiCall(`/artschool/families/${familyID}`);
      setFamilyDetail(data);
    } catch { /* */ }
  }, [apiCall]);

  // Load on tab change + year change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadDashboard();
    loadCategories();
    loadTeachers();
    loadClasses();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (activeTab === 'enrollments') loadEnrollments();
    if (activeTab === 'payments')    loadPayments();
    if (activeTab === 'families') {
      setSearching(true);
      apiCall('/artschool/families/search?q=%25')
        .then(data => setFamilyResults(data))
        .catch(() => setFamilyResults([]))
        .finally(() => setSearching(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedYear, statusFilter, classFilter]);

  // Family search debounce
  useEffect(() => {
    if (familySearch.trim().length < 2) { setFamilyResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        setFamilyResults(await apiCall(`/artschool/families/search?q=${encodeURIComponent(familySearch)}`));
      } catch { setFamilyResults([]); }
      finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familySearch, apiCall]);

  // When a family is selected in families tab, load detail
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (selectedFamily && activeTab === 'families') loadFamilyDetail(selectedFamily.FamilyID);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFamily, activeTab]);

  // ----------------------------------------------------------------
  // Handlers
  // ----------------------------------------------------------------
  const handleSaveTeacher = async (form) => {
    try {
      if (editTeacher) {
        await apiCall(`/artschool/teachers/${editTeacher.TeacherID}`, { method: 'PUT', body: JSON.stringify(form) });
        showSuccess('Teacher updated');
      } else {
        await apiCall('/artschool/teachers', { method: 'POST', body: JSON.stringify(form) });
        showSuccess('Teacher added');
      }
      loadTeachers();
    } catch (err) { showError(err.message); throw err; }
  };

  const handleSaveClass = async (form) => {
    try {
      if (editClass) {
        await apiCall(`/artschool/classes/${editClass.ClassID}`, { method: 'PUT', body: JSON.stringify(form) });
        showSuccess('Class updated');
      } else {
        await apiCall('/artschool/classes', { method: 'POST', body: JSON.stringify(form) });
        showSuccess('Class created');
      }
      loadClasses(); loadDashboard();
    } catch (err) { showError(err.message); throw err; }
  };

  const handleSaveFamily = async (data) => {
    try {
      let artStudentID = null;
      let familyID     = null;
      let studentName  = '';

      if (data.mode === 'school') {
        const { child } = data;
        familyID    = data.familyID;
        studentName = child.FullName;

        if (child.AlreadyInArtSchool) {
          // Already registered — use existing ArtStudentID
          artStudentID = child.ArtStudentID;
          showSuccess(`${child.FullName} already registered — select a class to enroll.`);
        } else {
          // Create ArtSchoolStudents record
          const res = await apiCall('/artschool/students', {
            method: 'POST',
            body: JSON.stringify({
              familyID,
              firstName:   child.FirstName,
              lastName:    child.LastName,
              dateOfBirth: child.DateOfBirth || null,
              gender:      child.Gender      || null,
              studentID:   child.StudentID,
            })
          });
          artStudentID = res.artStudentID;
          showSuccess(`${child.FullName} registered in art school.`);
        }
      } else {
        // External family — create family + student
        familyID    = data.familyID;
        studentName = data.isAdult
          ? data.studentFirstName
          : `${data.studentFirstName} ${data.studentLastName}`.trim();

        const res = await apiCall('/artschool/families', {
          method: 'POST',
          body: JSON.stringify({
            familyID:           data.familyID,
            fatherName:         data.fatherName      || null,
            motherName:         data.motherName      || null,
            primaryPhone:       data.primaryPhone    || null,
            email:              data.email           || null,
            address:            data.address         || null,
            notes:              data.notes           || null,
            isArtSchoolOnly:    data.isArtSchoolOnly ? 1 : 0,
            studentFirstName:   data.studentFirstName,
            studentLastName:    data.studentLastName,
            studentDateOfBirth: data.studentDOB      || null,
            studentGender:      data.studentGender   || null,
          })
        });
        artStudentID = res.artStudentID;
        showSuccess('Family registered successfully.');
      }

      setFamilySearch('');
      setFamilyResults([]);
      setSelectedFamily(null);
      setFamilyDetail(null);
      loadDashboard();
      // Reload family list
      apiCall('/artschool/families/search?q=%25')
        .then(d => setFamilyResults(d))
        .catch(() => {});

      // Auto-open enroll modal if we have a student
      if (artStudentID && familyID) {
        // Fetch the family detail to get the family object for the modal
        const familyDetail = await apiCall(`/artschool/families/${familyID}`);
        const student = familyDetail.students?.find(s => s.ArtStudentID === artStudentID)
          || { ArtStudentID: artStudentID, FirstName: studentName, LastName: '' };
        setEnrollModal({ student, family: familyDetail.family });
      }

    } catch (err) { showError(err.message); throw err; }
  };

  const handleEnroll = async (data) => {
    try {
      await apiCall('/artschool/enrollments', { method: 'POST', body: JSON.stringify(data) });
      showSuccess('Student enrolled successfully');
      loadEnrollments(); loadDashboard();
      if (selectedFamily) loadFamilyDetail(selectedFamily.FamilyID);
    } catch (err) { showError(err.message); throw err; }
  };

  const handleCancelEnrollment = async (enrollmentID, familyID) => {
    if (!window.confirm('Cancel this enrollment?')) return;
    try {
      await apiCall(`/artschool/enrollments/${enrollmentID}`, {
        method: 'PUT',
        body: JSON.stringify({ status: 'Cancelled', cancellationReason: 'Cancelled by admin' })
      });
      showSuccess('Enrollment cancelled');
      loadEnrollments(); loadDashboard();
    } catch (err) { showError(err.message); }
  };

  // ----------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------
  return (
    <div className="space-y-6">
      {/* Module Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 rounded-xl">
            <Music size={24} className="text-indigo-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800">Art School</h2>
            <p className="text-sm text-gray-500">Music · Dance · Fine Arts · Digital Innovation</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canReport && (
            <button
              onClick={() => setReportModal(true)}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100"
            >
              <BarChart2 size={16} /> Report
            </button>
          )}
          {canEnroll && (
            <button
              onClick={() => setFamilyModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              <Plus size={16} /> Register Family
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium flex-1 justify-center transition-colors
                ${activeTab === tab.id
                  ? 'bg-white text-indigo-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'}`}
            >
              <Icon size={16} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* ============================================================ */}
      {/* DASHBOARD TAB                                                 */}
      {/* ============================================================ */}
      {activeTab === 'dashboard' && (
        <div className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <DashCard label="Active Enrollments" value={dashboard?.ActiveEnrollments ?? '—'} color="indigo" />
            <DashCard label="Active Students"    value={dashboard?.ActiveStudents    ?? '—'} color="blue"   />
            <DashCard label="Active Families"    value={dashboard?.ActiveFamilies    ?? '—'} color="green"  />
            <DashCard label="Classes Open"       value={dashboard?.TotalClasses      ?? '—'} color="purple" />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <DashCard label="Total Charged"     value={`$${parseFloat(dashboard?.TotalCharged    || 0).toFixed(2)}`} color="gray"   />
            <DashCard label="Total Collected"   value={`$${parseFloat(dashboard?.TotalCollected  || 0).toFixed(2)}`} color="green"  />
            <DashCard label="Outstanding"       value={`$${parseFloat(dashboard?.TotalOutstanding|| 0).toFixed(2)}`} color="red"    />
          </div>

          {/* Quick: Classes by category */}
          {classes.length > 0 && (
            <div className="bg-white rounded-xl border p-5">
              <h3 className="font-semibold text-gray-700 mb-4">Classes — {selectedYear}</h3>
              <div className="space-y-2">
                {categories.map(cat => {
                  const catClasses = classes.filter(c => c.CategoryID === cat.CategoryID);
                  if (catClasses.length === 0) return null;
                  return (
                    <div key={cat.CategoryID}>
                      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                        {cat.CategoryName}
                      </div>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {catClasses.map(cls => (
                          <div key={cls.ClassID} className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50 border border-indigo-100 rounded-lg text-sm">
                            <span className="font-medium text-indigo-800">{cls.ClassName}</span>
                            <span className="text-indigo-400 text-xs">{cls.ClassType}</span>
                            {cls.EnrolledCount > 0 && (
                              <span className="bg-indigo-200 text-indigo-700 text-xs px-1.5 py-0.5 rounded-full">
                                {cls.EnrolledCount}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ============================================================ */}
      {/* CLASSES TAB                                                   */}
      {/* ============================================================ */}
      {activeTab === 'classes' && (
        <div className="space-y-4">
          {/* Teachers section */}
          <div className="bg-white rounded-xl border p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-700">Teachers</h3>
              {canAdmin && (
                <button
                  onClick={() => { setEditTeacher(null); setTeacherModal(true); }}
                  className="flex items-center gap-1 text-sm px-3 py-1.5 bg-purple-50 text-purple-700 border border-purple-200 rounded-lg hover:bg-purple-100"
                >
                  <Plus size={14} /> Add Teacher
                </button>
              )}
            </div>
            {teachers.length === 0 ? (
              <p className="text-sm text-gray-400">No teachers yet. Add your first teacher.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {teachers.map(t => (
                  <div key={t.TeacherID} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 rounded-full">
                        <User size={16} className="text-purple-600" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-800">
                          {t.FirstName} {t.LastName}
                        </div>
                        <div className="text-xs text-gray-500">
                          {t.Phone || t.Email || 'No contact info'}
                          {t.AssignedClasses > 0 && ` · ${t.AssignedClasses} class${t.AssignedClasses > 1 ? 'es' : ''}`}
                        </div>
                      </div>
                    </div>
                    {canAdmin && (
                      <button
                        onClick={() => { setEditTeacher(t); setTeacherModal(true); }}
                        className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400"
                      >
                        <Edit size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Classes by category */}
          <div className="bg-white rounded-xl border p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-700">Classes — {selectedYear}</h3>
              {canAdmin && (
                <button
                  onClick={() => { setEditClass(null); setClassModal(true); }}
                  className="flex items-center gap-1 text-sm px-3 py-1.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-100"
                >
                  <Plus size={14} /> Add Class
                </button>
              )}
            </div>

            {classes.length === 0 ? (
              <p className="text-sm text-gray-400">No classes yet for {selectedYear}.</p>
            ) : (
              <div className="space-y-5">
                {categories.map(cat => {
                  const catClasses = classes.filter(c => c.CategoryID === cat.CategoryID);
                  if (catClasses.length === 0) return null;
                  return (
                    <div key={cat.CategoryID}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                          {cat.CategoryName}
                        </span>
                        <div className="text-xs text-gray-400">
                          Monthly: Private ${cat.PrivateMonthly ?? '—'} ·
                          SmallGroup ${cat.SmallGroupMonthly ?? '—'} ·
                          Group ${cat.GroupMonthly ?? '—'}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {catClasses.map(cls => (
                          <div key={cls.ClassID} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                            <div>
                              <div className="text-sm font-medium text-gray-800 flex items-center gap-2">
                                {cls.ClassName}
                                <span className={`text-xs px-1.5 py-0.5 rounded-full
                                  ${cls.ClassType === 'Private'    ? 'bg-purple-100 text-purple-700'
                                  : cls.ClassType === 'SmallGroup' ? 'bg-blue-100 text-blue-700'
                                  : 'bg-green-100 text-green-700'}`}>
                                  {cls.ClassType}
                                </span>
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5 space-y-0.5">
                                {cls.TeacherName && <span className="mr-2">👤 {cls.TeacherName}</span>}
                                {cls.Schedule    && <span>🕐 {cls.Schedule}</span>}
                              </div>
                              <div className="text-xs text-gray-400 mt-0.5">
                                {cls.EnrolledCount || 0} enrolled
                                {cls.MaxStudents ? ` / ${cls.MaxStudents} max` : ''}
                                {cls.MonthlyPrice ? ` · $${cls.MonthlyPrice}/mo` : ''}
                              </div>
                            </div>
                            {canAdmin && (
                              <button
                                onClick={() => { setEditClass(cls); setClassModal(true); }}
                                className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 ml-2"
                              >
                                <Edit size={14} />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* FAMILIES TAB                                                  */}
      {/* ============================================================ */}
      {activeTab === 'families' && (
        <div className="space-y-4">
          {/* Search */}
          <div className="bg-white rounded-xl border p-5">
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-3 text-gray-400" />
                <input
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="Search family by name or phone..."
                  value={familySearch}
                  onChange={e => { setFamilySearch(e.target.value); setSelectedFamily(null); setFamilyDetail(null); }}
                />
                {searching && <RefreshCw size={14} className="absolute right-3 top-3 text-gray-400 animate-spin" />}
              </div>
              {canEnroll && (
                <button
                  onClick={() => setFamilyModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm whitespace-nowrap"
                >
                  <Plus size={16} /> New Family
                </button>
              )}
            </div>

            {/* Search results */}
            {familyResults.length > 0 && !selectedFamily && (
              <div className="mt-3 border rounded-lg divide-y max-h-64 overflow-y-auto">
                {familyResults.map(f => (
                  <button
                    key={f.FamilyID}
                    onClick={() => { setSelectedFamily(f); setFamilySearch(f.FamilyDisplayName || f.FamilyID); }}
                    className="w-full text-left px-4 py-3 hover:bg-indigo-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium text-gray-800">{f.FamilyDisplayName || f.FamilyID}</span>
                        <span className="text-xs text-gray-400 ml-2">{f.FamilyID}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        {f.ArtStudentCount > 0 && (
                          <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
                            {f.ArtStudentCount} student{f.ArtStudentCount > 1 ? 's' : ''}
                          </span>
                        )}
                        {f.ArtSchoolBalance > 0 && (
                          <span className="text-red-600 font-medium">${f.ArtSchoolBalance.toFixed(2)} due</span>
                        )}
                        <ChevronRight size={14} />
                      </div>
                    </div>
                    {f.PrimaryPhone && <div className="text-xs text-gray-400 mt-0.5">{f.PrimaryPhone}</div>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Family Detail */}
          {selectedFamily && familyDetail && (
            <div className="bg-white rounded-xl border overflow-hidden">
              {/* Family header */}
              <div className="flex items-center justify-between px-5 py-4 bg-gray-50 border-b">
                <div>
                  <h3 className="font-bold text-gray-800">
                    {familyDetail.family.FatherName || ''} {familyDetail.family.FatherName && familyDetail.family.MotherName ? '&' : ''} {familyDetail.family.MotherName || ''}
                  </h3>
                  <div className="text-xs text-gray-500 flex gap-3 mt-0.5">
                    <span>{familyDetail.family.FamilyID}</span>
                    {familyDetail.family.PrimaryPhone && <span>{familyDetail.family.PrimaryPhone}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="text-xs text-gray-500">Art School Balance</div>
                    <div className={`font-bold ${familyDetail.family.ArtBalance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      ${parseFloat(familyDetail.family.ArtBalance || 0).toFixed(2)}
                    </div>
                  </div>
                  {canPay && familyDetail.family.ArtBalance > 0 && (
                    <div className="flex items-center gap-1 px-3 py-1.5 bg-amber-50 border border-amber-200 text-amber-700 rounded-lg text-xs">
                      <CreditCard size={13} /> Use Payments module to pay
                    </div>
                  )}
                  <button
                    onClick={() => { setSelectedFamily(null); setFamilyDetail(null); setFamilySearch(''); }}
                    className="p-1.5 hover:bg-gray-200 rounded-lg"
                  >
                    <X size={16} className="text-gray-400" />
                  </button>
                </div>
              </div>

              {/* Students */}
              <div className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-700">Students</h4>
                  {canEnroll && (
                    <button
                      onClick={async () => {
                        const name = prompt('Student first name:');
                        if (!name) return;
                        const last = prompt('Student last name:');
                        if (!last) return;
                        try {
                          await apiCall('/artschool/students', {
                            method: 'POST',
                            body: JSON.stringify({ familyID: familyDetail.family.FamilyID, firstName: name, lastName: last })
                          });
                          showSuccess('Student added');
                          loadFamilyDetail(familyDetail.family.FamilyID);
                        } catch (err) { showError(err.message); }
                      }}
                      className="flex items-center gap-1 text-xs px-2.5 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      <Plus size={12} /> Add Student
                    </button>
                  )}
                </div>

                {familyDetail.students.length === 0 ? (
                  <p className="text-sm text-gray-400">No art school students registered yet.</p>
                ) : (
                  <div className="space-y-3">
                    {familyDetail.students.map(student => (
                      <div key={student.ArtStudentID} className="border rounded-lg overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-3 bg-indigo-50">
                          <div className="flex items-center gap-2">
                            <div className="p-1.5 bg-indigo-200 rounded-full">
                              <User size={14} className="text-indigo-700" />
                            </div>
                            <span className="font-medium text-indigo-900">
                              {student.FirstName} {student.LastName}
                            </span>
                            {student.ActiveEnrollments > 0 && (
                              <span className="bg-indigo-200 text-indigo-700 text-xs px-1.5 py-0.5 rounded-full">
                                {student.ActiveEnrollments} class{student.ActiveEnrollments > 1 ? 'es' : ''}
                              </span>
                            )}
                            {student.ActiveEnrollments > 0 && (
                              <span className="flex items-center gap-0.5 text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full">
                                <Tag size={10} /> Discount eligible
                              </span>
                            )}
                          </div>
                          {canEnroll && (
                            <button
                              onClick={() => setEnrollModal({ student, family: familyDetail.family })}
                              className="flex items-center gap-1 text-xs px-2.5 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                            >
                              <Plus size={12} /> Enroll
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ============================================================ */}
      {/* ENROLLMENTS TAB                                               */}
      {/* ============================================================ */}
      {activeTab === 'enrollments' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex gap-3 bg-white rounded-xl border p-4">
            <select
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="Active">Active</option>
              <option value="Cancelled">Cancelled</option>
              <option value="Completed">Completed</option>
            </select>
            <select
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
              value={classFilter}
              onChange={e => setClassFilter(e.target.value)}
            >
              <option value="">All Classes</option>
              {classes.map(c => (
                <option key={c.ClassID} value={c.ClassID}>{c.ClassName} ({c.CategoryName})</option>
              ))}
            </select>
            <button onClick={loadEnrollments} className="p-2 hover:bg-gray-100 rounded-lg">
              <RefreshCw size={16} className="text-gray-500" />
            </button>
            <div className="ml-auto text-sm text-gray-500 flex items-center">
              {enrollments.length} enrollment{enrollments.length !== 1 ? 's' : ''}
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl border overflow-hidden">
            {enrollments.length === 0 ? (
              <div className="text-center py-12 text-gray-400">No enrollments found.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">Student</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">Family</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">Class</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">Billing</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-600">Price</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-600">Status</th>
                      {canAdmin && <th className="px-4 py-3 text-center font-semibold text-gray-600">Actions</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {enrollments.map(e => (
                      <tr key={e.EnrollmentID} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-800">{e.StudentName}</td>
                        <td className="px-4 py-3 text-gray-600">
                          <div>{e.FamilyDisplayName}</div>
                          <div className="text-xs text-gray-400">{e.PrimaryPhone}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-700">{e.ClassName}</div>
                          <div className="text-xs text-gray-400">{e.CategoryName} · {e.ClassType}</div>
                          {e.Schedule && <div className="text-xs text-gray-400">{e.Schedule}</div>}
                        </td>
                        <td className="px-4 py-3">
                          <div>{e.BillingType}</div>
                          {e.DiscountPct > 0 && (
                            <div className="text-xs text-green-600 flex items-center gap-0.5">
                              <Tag size={10} /> {e.DiscountPct}% {e.DiscountReason}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="font-medium text-gray-800">${parseFloat(e.FinalPrice).toFixed(2)}</div>
                          {e.BalanceRemaining > 0 && (
                            <div className="text-xs text-red-500">${parseFloat(e.BalanceRemaining).toFixed(2)} due</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium
                            ${e.Status === 'Active'    ? 'bg-green-100 text-green-700'
                            : e.Status === 'Cancelled' ? 'bg-red-100 text-red-700'
                            : 'bg-gray-100 text-gray-600'}`}>
                            {e.Status}
                          </span>
                        </td>
                        {canAdmin && (
                          <td className="px-4 py-3 text-center">
                            {e.Status === 'Active' && (
                              <button
                                onClick={() => handleCancelEnrollment(e.EnrollmentID, e.FamilyID)}
                                className="text-xs text-red-500 hover:text-red-700 flex items-center gap-0.5 mx-auto"
                              >
                                <X size={12} /> Cancel
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* PAYMENTS TAB                                                  */}
      {/* ============================================================ */}
      {activeTab === 'payments' && (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b">
            <h3 className="font-semibold text-gray-700">Payments — {selectedYear}</h3>
            <button onClick={loadPayments} className="p-2 hover:bg-gray-100 rounded-lg">
              <RefreshCw size={16} className="text-gray-500" />
            </button>
          </div>
          {payments.length === 0 ? (
            <div className="text-center py-12 text-gray-400">No payments recorded yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Date</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Family</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-600">Amount</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Method</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">By</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {payments.map(p => (
                    <tr key={p.PaymentID} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-600">
                        {new Date(p.PaymentDate).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-800">
                        {p.FatherName || p.MotherName || p.FamilyID}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-green-700">
                        ${parseFloat(p.Amount).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{p.PaymentMethod}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{p.CreatedByName || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ============================================================ */}
      {/* MODALS                                                        */}
      {/* ============================================================ */}
      {teacherModal && (
        <ArtSchoolTeacherModal
          teacher={editTeacher}
          onClose={() => { setTeacherModal(false); setEditTeacher(null); }}
          onSave={handleSaveTeacher}
        />
      )}

      {classModal && (
        <ArtSchoolClassModal
          cls={editClass}
          categories={categories}
          teachers={teachers}
          academicYear={selectedYear}
          onClose={() => { setClassModal(false); setEditClass(null); }}
          onSave={handleSaveClass}
        />
      )}

      {familyModal && (
        <ArtSchoolFamilyModal
          apiCall={apiCall}
          selectedYear={selectedYear}
          onClose={() => setFamilyModal(false)}
          onSave={handleSaveFamily}
        />
      )}

      {enrollModal && (
        <ArtSchoolEnrollModal
          student={enrollModal.student}
          family={enrollModal.family}
          classes={classes}
          academicYear={selectedYear}
          apiCall={apiCall}
          onClose={() => setEnrollModal(null)}
          onSave={handleEnroll}
        />
      )}

      {reportModal && (
        <ArtSchoolReportModal
          academicYear={selectedYear}
          apiCall={apiCall}
          onClose={() => setReportModal(false)}
        />
      )}
    </div>
  );
};

// Small dashboard stat card
const DashCard = ({ label, value, color }) => {
  const colors = {
    indigo: 'border-indigo-100 bg-indigo-50',
    blue:   'border-blue-100   bg-blue-50',
    green:  'border-green-100  bg-green-50',
    purple: 'border-purple-100 bg-purple-50',
    gray:   'border-gray-100   bg-gray-50',
    red:    'border-red-100    bg-red-50'
  };
  return (
    <div className={`rounded-xl border p-4 ${colors[color] || colors.gray}`}>
      <div className="text-2xl font-bold text-gray-800">{value}</div>
      <div className="text-xs text-gray-500 mt-0.5">{label}</div>
    </div>
  );
};

export default ArtSchool;
